import React, { useState } from 'react';
import { profileService } from '../../../services/profileService';
import { useAuth } from '../../../context/AuthContext';
import { EyeOpen, EyeClosed } from '../../../public/icons';

const Security = () => {
  const { logout } = useAuth();
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Please fill in all password fields' });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'New password must be at least 6 characters' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await profileService.updatePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      
      if (response.success) {
        setMessage({ type: 'success', text: 'Password updated successfully!' });
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        setShowPasswords({
          current: false,
          new: false,
          confirm: false,
        });
      } else {
        setMessage({ type: 'error', text: response.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const response = await profileService.deleteAccount();
      if (response.success) {
        logout();
      } else {
        setMessage({ type: 'error', text: response.message });
        setShowDeleteConfirm(false);
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="px-2 sm:px-0">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Security & Login</h1>
      <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
        Manage your password and account protection.
      </p>

      {message.text && (
        <div
          className={`mb-4 sm:mb-6 p-3 sm:p-4 text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="w-full">
        {/* Change Password Section */}
        <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-4">Change Password</h2>
        <div className="border-b border-gray-200 mb-6"></div>

        <form onSubmit={handlePasswordSubmit} className="space-y-5 sm:space-y-6 mb-8">
          {/* Current Password */}
          <div>
            <label className="block text-sm font-normal text-secondary mb-2">
              Current Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.current ? "text" : "password"}
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                className="w-full p-3 sm:p-4 bg-[#F5F5F5] border border-[#E4E4E4] focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-colors pr-10 sm:pr-12 text-sm sm:text-base"
                placeholder="********"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                disabled={loading}
              >
                {showPasswords.current ? (
                  <EyeClosed className="w-4 h-4 sm:w-5 sm:h-5" />
                ) : (
                  <EyeOpen className="w-4 h-4 sm:w-5 sm:h-5" />
                )}
              </button>
            </div>
          </div>
          
          {/* New Password */}
          <div>
            <label className="block text-sm font-normal text-secondary mb-2">
              New password
            </label>
            <div className="relative">
              <input
                type={showPasswords.new ? "text" : "password"}
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                className="w-full p-3 sm:p-4 bg-[#F5F5F5] border border-[#E4E4E4] focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-colors pr-10 sm:pr-12 text-sm sm:text-base"
                placeholder="*****"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                disabled={loading}
              >
                {showPasswords.new ? (
                  <EyeClosed className="w-4 h-4 sm:w-5 sm:h-5" />
                ) : (
                  <EyeOpen className="w-4 h-4 sm:w-5 sm:h-5" />
                )}
              </button>
            </div>
          </div>
          
          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-normal text-secondary mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? "text" : "password"}
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                className="w-full p-3 sm:p-4 bg-[#F5F5F5] border border-[#E4E4E4] focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-colors pr-10 sm:pr-12 text-sm sm:text-base"
                placeholder="******"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                disabled={loading}
              >
                {showPasswords.confirm ? (
                  <EyeClosed className="w-4 h-4 sm:w-5 sm:h-5" />
                ) : (
                  <EyeOpen className="w-4 h-4 sm:w-5 sm:h-5" />
                )}
              </button>
            </div>
          </div>
          
          <div className="pt-4">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 w-full sm:w-auto min-w-[200px] bg-[#D2A146] text-white font-medium hover:bg-[#C49564] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
            >
              {loading ? 'Changing Password...' : 'Change Password'}
            </button>
          </div>
        </form>

        {/* Delete Account Section */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pt-6 sm:pt-8 border-t border-gray-200">
          <div>
            <h2 className="text-base sm:text-lg font-medium text-secondary mb-2">Delete Account</h2>
            <p className="text-sm text-[#6B635D]">
              NOTE: Account will NOT BE RECOVERABLE once deleted.
            </p>
          </div>

          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-5 sm:px-6 py-2.5 sm:py-3 bg-[#FF5F57] text-white font-medium hover:bg-[#E04E47] transition-colors duration-200 rounded text-sm"
            disabled={loading}
          >
            Delete Account
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-5 sm:p-6 mx-4">
            <h3 className="text-base sm:text-lg font-semibold text-red-600 mb-2">
              Confirm Account Deletion
            </h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4">
              Are you sure you want to delete your account? This action cannot be undone.
              All your data, orders, and memoirs will be permanently deleted.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleDeleteAccount}
                className="px-4 py-2 bg-red-600 text-white font-medium rounded hover:bg-red-700 transition-colors flex-1 text-sm"
              >
                Yes, Delete Account
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded hover:bg-gray-50 transition-colors flex-1 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Security;